import React from 'react';
import { Dialog, DialogContent } from '../atoms/dialog';
import { DropdownMenuSeparator } from '../atoms/dropdown-menu';
import { Button } from '../atoms/button';

const loadingSpinner = () => (
  <svg
    className="svg-spinner"
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M10 1.875V3.95833M10 15V18.3333M4.79167 10H1.875M17.7083 10H16.4583M15.3809 15.3809L14.7917 14.7917M15.5535 4.51316L14.375 5.69167M4.10131 15.8987L6.45833 13.5417M4.2739 4.34057L6.04167 6.10833"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export type DisconnectDialogProps = {
  open: boolean;
  onClose?: () => void;
  connectedFolder?: string | null;
  onDisconnect?: () => void;
};

const DisconnectDialog: React.FC<DisconnectDialogProps> = ({
  open,
  onClose,
  connectedFolder = null,
  onDisconnect,
}) => {
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [isDisconnecting, setIsDisconnecting] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setConfirmOpen(false);
      setIsDisconnecting(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen && onClose) onClose(); }}>
      <DialogContent className={`p-6 gap-4 transition-none ${confirmOpen ? 'w-[300px]' : 'max-w-md'}`}>

        {!confirmOpen ? (
          <>
            {/* ------------ SCREEN 1 ------------ */}
            <div className="flex justify-center">
              <div className="w-8 h-8 bg-[#FFFAEB] rounded-sm flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 13" fill="none">
                  <path d="M6.8757 4.03217L4.71265 0.214845C4.78447 0.141723 4.8671 0.0937604 4.94973 0.0615234C4.31186 0.271457 4.01917 0.987745 4.01917 0.987745L0.0676074 7.95957C0.0120059 8.17815 -0.00344032 8.37551 0.00350987 8.54927H4.31649L6.8757 4.03217Z" fill="#34A853" />
                  <path d="M6.87695 4.03217L9.43617 8.54927H13.7491C13.7561 8.37551 13.7406 8.17815 13.685 7.95957L9.73348 0.987745C9.73348 0.987745 9.44003 0.271457 8.80293 0.0615234C8.88479 0.0937604 8.96819 0.141723 9.04001 0.214845L6.87695 4.03217Z" fill="#FBBC05" />
                  <path d="M6.87594 4.03214L9.039 0.214816C8.96718 0.141693 8.88378 0.0937309 8.80192 0.061494C8.70771 0.0308296 8.60654 0.0103867 8.49688 0.00488281H8.38259H5.3693H5.255C5.14612 0.00960041 5.04418 0.0300433 4.94997 0.061494C4.86811 0.0937309 4.78471 0.141693 4.71289 0.214816L6.87594 4.03214Z" fill="#188038" />
                  <path d="M4.31764 8.54883L2.1793 12.3229C2.1793 12.3229 2.10825 12.2883 2.01172 12.2168C2.31521 12.4503 2.61021 12.4998 2.61021 12.4998H11.0084C11.4717 12.4998 11.5682 12.3229 11.5682 12.3229C11.5698 12.3221 11.5705 12.3213 11.5721 12.3205L9.4353 8.54883H4.31764Z" fill="#4285F4" />
                  <path d="M4.31884 8.54883H0.00585938C0.0274822 9.06147 0.249889 9.36104 0.249889 9.36104L0.41206 9.64253C0.423644 9.65904 0.430593 9.66847 0.430593 9.66847L0.783509 10.288L1.57583 11.6672C1.599 11.723 1.62603 11.7741 1.6546 11.8228C1.66541 11.8394 1.67545 11.8574 1.68703 11.8732C1.69012 11.8779 1.69321 11.8826 1.6963 11.8873C1.79437 12.0257 1.90403 12.1326 2.01369 12.2168C2.11022 12.2891 2.18127 12.3229 2.18127 12.3229L4.31884 8.54883Z" fill="#1967D2" />
                  <path d="M9.43555 8.54883H13.7485C13.7269 9.06147 13.5045 9.36104 13.5045 9.36104L13.3423 9.64253C13.3307 9.65904 13.3238 9.66847 13.3238 9.66847L12.9709 10.288L12.1786 11.6672C12.1554 11.723 12.1284 11.7741 12.0998 11.8228C12.089 11.8394 12.0789 11.8574 12.0673 11.8732C12.0643 11.8779 12.0612 11.8826 12.0581 11.8873C11.96 12.0257 11.8503 12.1326 11.7407 12.2168C11.6442 12.2891 11.5731 12.3229 11.5731 12.3229L9.43555 8.54883Z" fill="#EA4335" />
                </svg>
              </div>
            </div>

            <div className="text-center">
              <h2 className="text-base font-semibold text-gray-800 mb-2">Connected Google Drive Folder</h2>
              <p className="text-[12px] text-gray-800">You connected google drive folder successfully to the project</p>
            </div>

            <DropdownMenuSeparator className="bg-[#F5F5F5] my-0 mx-0" />

            <div className="p-2 bg-[#ECFDF3] border border-[#AAF0C4] rounded-sm">
              <div className="flex items-start gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 15 13" fill="none">
                  <path d="M13.8333 11.1667C13.8333 11.5203 13.6929 11.8594 13.4428 12.1095C13.1928 12.3595 12.8536 12.5 12.5 12.5H1.83333C1.47971 12.5 1.14057 12.3595 0.890524 12.1095C0.640476 11.8594 0.5 11.5203 0.5 11.1667V1.83333C0.5 1.47971 0.640476 1.14057 0.890524 0.890524C1.14057 0.640476 1.47971 0.5 1.83333 0.5H5.16667L6.5 2.5H12.5C12.8536 2.5 13.1928 2.64048 13.4428 2.89052C13.6929 3.14057 13.8333 3.47971 13.8333 3.83333V11.1667Z" stroke="#535862" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                <div className="flex-1">
                  <p className="text-xs text-[#414651] mb-1">Selected folder</p>
                  <p className="text-lg font-semibold text-[#414651]">{connectedFolder ?? '—'}</p>
                </div>
              </div>
            </div>

            <div className="flex items-end justify-end">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => { if (onClose) onClose(); }}
                  className="py-2 px-4 bg-white text-gray-800 font-semibold text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  Back
                </Button>

                <Button
                  onClick={() => setConfirmOpen(true)}
                  className="py-2 px-3 font-semibold text-sm rounded-md flex-1 bg-red-600 hover:bg-red-700 text-white border-[#ffffff1f]"
                >
                  Disconnect
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* ------------ SCREEN 2 (Confirm) ------------ */}
            <div className="flex items-center justify-start">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#101828" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <div className="text-start">
              <h2 className="text-base font-semibold text-gray-800 mb-3">
                Are you sure to disconnect<br />"{connectedFolder}" folder
              </h2>
              <p className="text-sm text-gray-800">
                Disconnecting will stop the sync and<br />impact the project context experience
              </p>
            </div>

            <div className="flex items-end justify-end">
              
              <div className="flex gap-2">

                {/*  Disconnect button with new background */}
                <Button
                  onClick={async () => {
                    if (onDisconnect) {
                      setIsDisconnecting(true);
                      try {
                        await onDisconnect();
                      } finally {
                        setIsDisconnecting(false);
                      }
                    }
                  }}
                  disabled={isDisconnecting}
                  className="py-2 px-4 font-semibold text-sm rounded-md flex-1 
                    bg-[#F9FAFB] hover:bg-[#F3F4F6] 
                    text-gray-800 border border-gray-300
                    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDisconnecting ? (
                    <div className="flex items-center justify-center gap-2">
                      {loadingSpinner()}
                      <span>Disconnecting...</span>
                    </div>
                  ) : (
                    'Disconnect'
                  )}
                </Button>

                {/* Continue → acts like cancel */}
                <Button
                  onClick={() => {
                    if (onClose) onClose();
                    setConfirmOpen(false);
                  }}
                  disabled={isDisconnecting}
                  className="py-2 px-3 font-semibold text-sm rounded-md flex-1 bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </Button>

              </div>
            </div>
          </>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default DisconnectDialog;
